/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { ExternalLink, RadioButton, RadioButtonGroup, Callout, Note } from "@octopusdeploy/design-system-components";
import { ParseHelper, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import type { RetentionPeriod } from "@octopusdeploy/octopus-server-client";
import { RetentionPeriodStrategy, RetentionUnit } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { client } from "~/clientInstance";
import ComponentRow from "~/components/ComponentRow";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import InternalLink from "~/components/Navigation/InternalLink";
import { Text } from "~/components/form";
import { minimum } from "~/components/form/Validators";
import styles from "./style.module.less";
interface RetentionPolicyControlProps {
    defaultReleaseRetentionPolicy: RetentionPeriod;
    defaultTentacleRetentionPolicy: RetentionPeriod;
    releaseRetentionPolicy: RetentionPeriod;
    tentacleRetentionPolicy: RetentionPeriod;
    onReleaseRetentionPolicyChange(policy: RetentionPeriod): void;
    onTentacleRetentionPolicyChange(policy: RetentionPeriod): void;
}
export default function RetentionPolicyControl({ releaseRetentionPolicy, tentacleRetentionPolicy, defaultReleaseRetentionPolicy, defaultTentacleRetentionPolicy, onReleaseRetentionPolicyChange, onTentacleRetentionPolicyChange, }: RetentionPolicyControlProps) {
    return (<div>
            <div>
                <RadioButtonGroup value={releaseRetentionPolicy.Strategy} accessibleName="Release Retention Policy" onChange={(newStrategy) => onReleaseRetentionPolicyChange({
            ...releaseRetentionPolicy,
            Strategy: newStrategy,
            ShouldKeepForever: newStrategy === RetentionPeriodStrategy.Forever,
            QuantityToKeep: newStrategy === RetentionPeriodStrategy.Count ? 3 : 0,
        })} title="How long should we keep releases?">
                    <RadioButton value={RetentionPeriodStrategy.Default} label={`Use default retention policy ${defaultRetentionPolicySummary(defaultReleaseRetentionPolicy)}`} isDefault={true}/>
                    <RadioButton value={RetentionPeriodStrategy.Forever} label="Keep all"/>
                    <RadioButton value={RetentionPeriodStrategy.Count} label="Keep a limited number"/>
                </RadioButtonGroup>
                {releaseRetentionPolicy.Strategy === RetentionPeriodStrategy.Count && (<ComponentRow>
                        <span className={styles.narrow}>
                            <Text type="number" accessibleName={releaseRetentionPolicy.Unit == RetentionUnit.Items ? "how many releases to keep" : "how many days to keep releases"} min={1} value={releaseRetentionPolicy.QuantityToKeep ? releaseRetentionPolicy.QuantityToKeep.toString() : ""} onChange={(x) => onReleaseRetentionPolicyChange({ ...releaseRetentionPolicy, QuantityToKeep: ParseHelper.safeParseInt(x) })} validate={minimum("Please provide a valid value for release retention.", 1)}/>
                        </span>
                        <span className={styles.narrow}>
                            <DeprecatedSelect value={releaseRetentionPolicy.Unit} onChange={(val) => onReleaseRetentionPolicyChange({ ...releaseRetentionPolicy, Unit: val as RetentionUnit })} items={[
                { value: RetentionUnit.Days, text: "Days" },
                { value: RetentionUnit.Items, text: "Releases" },
            ]}/>
                        </span>
                    </ComponentRow>)}
            </div>
            <div>
                <RadioButtonGroup value={tentacleRetentionPolicy.Strategy} onChange={(newStrategy) => onTentacleRetentionPolicyChange({
            ...tentacleRetentionPolicy,
            Strategy: newStrategy,
            ShouldKeepForever: newStrategy === RetentionPeriodStrategy.Forever,
            QuantityToKeep: newStrategy === RetentionPeriodStrategy.Count ? 3 : 0,
        })} accessibleName="Tentacle Retention Policy" title="How long should we keep extracted packages and files on disk on Tentacles?">
                    <RadioButton value={RetentionPeriodStrategy.Default} label={`Use default retention policy ${defaultRetentionPolicySummary(defaultTentacleRetentionPolicy)}`} isDefault={true}/>
                    <RadioButton value={RetentionPeriodStrategy.Forever} label="Keep all"/>
                    <RadioButton value={RetentionPeriodStrategy.Count} label="Keep a limited number"/>
                </RadioButtonGroup>
                {tentacleRetentionPolicy.Strategy === RetentionPeriodStrategy.Count && (<ComponentRow>
                        <span className={styles.narrow}>
                            <Text type="number" accessibleName={tentacleRetentionPolicy.Unit == RetentionUnit.Items ? "how many releases to keep packages on tentacles" : "how many days to keep packages on tentacles"} min={1} validate={minimum("Please provide a valid value for tentacle retention policy", 1)} value={tentacleRetentionPolicy.QuantityToKeep ? tentacleRetentionPolicy.QuantityToKeep.toString() : ""} onChange={(x) => onTentacleRetentionPolicyChange({ ...tentacleRetentionPolicy, QuantityToKeep: ParseHelper.safeParseInt(x) })}/>
                        </span>
                        <span className={styles.narrow}>
                            <DeprecatedSelect value={tentacleRetentionPolicy.Unit} onChange={(val) => onTentacleRetentionPolicyChange({ ...tentacleRetentionPolicy, Unit: val as RetentionUnit })} items={[
                { value: RetentionUnit.Days, text: "Days" },
                { value: RetentionUnit.Items, text: "Releases" },
            ]}/>
                        </span>
                    </ComponentRow>)}
                {isFeatureToggleEnabled("configurable-package-cache-retention") && (<Callout type={"new-feature"} title="Package cache retention is now configurable">
                        Package caching improves deployment speed and reduces network traffic. You can configure it in {<InternalLink to={links.machinePoliciesPage.generateUrl({ spaceId: client.spaceId ?? "" })}>Machine Policies</InternalLink>}. For
                        more details, refer to the <ExternalLink href="RetentionPolicies">retention policies</ExternalLink> documentation.
                    </Callout>)}
                <br />
                <Note>
                    Retention policies dictate how long releases and deployments are kept for. For more information please see <ExternalLink href="RetentionPolicies">retention policies</ExternalLink> documentation.
                </Note>
            </div>
        </div>);
}
function defaultRetentionPolicySummary(policy: RetentionPeriod): string {
    switch (policy.Strategy) {
        case RetentionPeriodStrategy.Forever:
            return "(keep all)";
        case RetentionPeriodStrategy.Count:
            return `(${policy.QuantityToKeep} ${policy.Unit === RetentionUnit.Days ? "days" : "releases"})`;
        default:
            return ""; // Default strategy should be either keep forever or count, so this case should not occur.
    }
}
